import React, { useCallback, useEffect, useState } from "react";
import { Outlet } from "@tanstack/react-router";
import { useIsMobile } from "@/hooks/use-mobile";
import TopAppBar from "./TopAppBar";
import Sidebar, { type SidebarItem } from "./Sidebar";
import CommandPalette from "./CommandPalette";

interface DashboardLayoutProps {
  title: string;
  sidebarItems: SidebarItem[];
  /** @deprecated Width is fixed by the shell; kept for compatibility. */
  sidebarWidth?: number;
  children?: React.ReactNode;
}

const COLLAPSE_KEY = "taskpart-sidebar-collapsed";

function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Application shell: the warm sidebar sits on the shell colour and the page
 * lives in a white, rounded panel beside it, with the top bar inside the panel.
 */
export default function DashboardLayout({ title, sidebarItems, children }: DashboardLayoutProps) {
  const isMobile = useIsMobile();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
  }, [isMobile]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        /* storage unavailable — keep the in-memory state */
      }
      return next;
    });
  }, []);

  return (
    <div className="flex h-[calc(100dvh-var(--banner-h,0px))] w-full overflow-hidden bg-background text-foreground md:bg-shell">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-full focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:text-primary-foreground"
      >
        Skip to content
      </a>

      <Sidebar
        items={sidebarItems}
        title={title}
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        collapsed={!isMobile && collapsed}
        onToggle={toggleCollapsed}
        isMobile={isMobile}
      />

      <div className="flex min-w-0 flex-1 flex-col md:py-3 md:pr-3">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background md:rounded-3xl md:border md:border-border md:shadow-panel">
          <TopAppBar
            title={title}
            items={sidebarItems}
            isMobile={isMobile}
            onMenuToggle={() => setMobileOpen(true)}
            onOpenSearch={() => setPaletteOpen(true)}
          />
          <main
            id="main-content"
            tabIndex={-1}
            className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden focus:outline-none"
          >
            <div className="mx-auto w-full max-w-[1440px] px-4 pt-5 pb-10 md:px-7 md:pt-6 [&>*]:min-w-0">
              {children || <Outlet />}
            </div>
          </main>
        </div>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} items={sidebarItems} />
    </div>
  );
}
